import { supabase } from '../lib/supabase';

/**
 * Generate a unique 8-character access code
 */
function generateAccessCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Register a new company and admin user
 */
export async function registerCompany(companyName, adminEmail, adminPassword) {
  try {
    // 1. Sign up admin user with Supabase Auth FIRST
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: adminEmail,
      password: adminPassword,
    });

    if (authError) {
      console.error('Auth signup error:', authError);
      throw new Error(authError.message || 'Failed to create account');
    }

    if (!authData.user) {
      throw new Error('User creation failed - no user returned');
    }

    // CRITICAL: Explicitly set the session to ensure RLS policies recognize the user
    await supabase.auth.setSession({
      access_token: authData.session.access_token,
      refresh_token: authData.session.refresh_token
    });
    
    // Wait a moment for session to propagate
    await new Promise(resolve => setTimeout(resolve, 500));

    // 2. Create company with explicit access code
    const accessCode = generateAccessCode();
    const { data: company, error: companyError } = await supabase
      .from('companies')
      .insert([{ 
        name: companyName,
        employee_access_code: accessCode
      }])
      .select()
      .single();

    if (companyError) {
      console.error('Company creation error:', {
        message: companyError.message,
        details: companyError.details,
        hint: companyError.hint,
        code: companyError.code
      });
      // Provide more specific error message
      if (companyError.code === 'PGRST301' || companyError.message.includes('permission')) {
        throw new Error('Unable to create company. Please ensure your account is confirmed.');
      }
      throw new Error(companyError.message || 'Failed to create company');
    }

    // 3. Create manager profile
    const { error: managerError } = await supabase
      .from('managers')
      .insert([{
        id: authData.user.id,
        company_id: company.id,
        email: adminEmail,
        role: 'admin'
      }]);

    if (managerError) {
      console.error('Manager profile error:', {
        message: managerError.message,
        details: managerError.details,
        hint: managerError.hint,
        code: managerError.code
      });
      throw new Error(managerError.message || 'Failed to create manager profile');
    }

    return {
      session: authData.session,
      company: company,
      manager: {
        id: authData.user.id,
        email: adminEmail,
        role: 'admin',
        company_id: company.id
      }
    };
  } catch (error) {
    console.error('Registration error:', error);
    throw error;
  }
}

/**
 * Send welcome email to new manager after registration
 */
export async function sendWelcomeEmail(companyName, adminEmail, accessCode) {
  try {
    await fetch(`/api/email/welcome`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: adminEmail,
        company_name: companyName,
        access_code: accessCode
      })
    });
  } catch (error) {
    console.error('Welcome email error (non-blocking):', error);
  }
}

/**
 * Login manager
 */
export async function loginManager(email, password) {
  try {
    // Step 1: Authenticate with Supabase
    const { data: authData, error: authError} = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (authError) {
      console.error('Login auth error:', authError);
      throw new Error(authError.message || 'Invalid email or password');
    }

    if (!authData.user) {
      throw new Error('Login failed - no user returned');
    }

    // Step 2: Wait for session to be established
    await new Promise(resolve => setTimeout(resolve, 300));

    // Step 3: Fetch manager profile with established session (with company joined)
    const { data: manager, error: managerError } = await supabase
      .from('managers')
      .select('*, company:companies(id, name, employee_access_code, is_active)')
      .eq('id', authData.user.id)
      .single();

    if (managerError || !manager) {
      console.error('Manager fetch error:', managerError);
      throw new Error('Unable to load manager profile. Please try again.');
    }

    // Step 3.5: If the user changed their auth email (Supabase confirmation flow),
    // sync managers.email so dashboards reflect the new address.
    const authEmail = authData.user.email?.toLowerCase();
    if (authEmail && manager.email?.toLowerCase() !== authEmail) {
      const { data: synced, error: syncError } = await supabase
        .from('managers')
        .update({ email: authEmail })
        .eq('id', authData.user.id)
        .select()
        .single();
      if (!syncError && synced) {
        manager.email = synced.email;
      }
    }

    // Step 4: Return both session and manager
    return {
      session: authData.session,
      manager
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
}

/**
 * Logout manager
 */
export async function logoutManager() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
}

/**
 * Get current manager profile (with company joined)
 */
export async function getCurrentManager() {
  const session = await getCurrentSession();
  if (!session) return null;

  // Add small delay to ensure session is fully established
  await new Promise(resolve => setTimeout(resolve, 100));

  const { data: manager, error } = await supabase
    .from('managers')
    .select('*, company:companies(id, name, employee_access_code, is_active)')
    .eq('id', session.user.id)
    .maybeSingle();

  if (error) {
    console.error('Error fetching manager:', error);
  }

  return manager;
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(email) {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      console.error('Password reset request error:', error);
      throw new Error(error.message || 'Failed to send reset email');
    }

    return { success: true };
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

/**
 * Update password
 */
export async function updatePassword(newPassword) {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      console.error('Password update error:', error);
      throw new Error(error.message || 'Failed to update password');
    }

    return { success: true };
  } catch (error) {
    console.error('Password update error:', error);
    throw error;
  }
}

